/**
 * Copyright (c) 2010-2020 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import { AsyncPipe, NgClass, NgTemplateOutlet } from '@angular/common';
import { Component, inject, Input, OnInit, output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatOption } from '@angular/material/autocomplete';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatSelect } from '@angular/material/select';
import { ClassDirective } from '@ngbracket/ngx-layout';
import { FlexDirective, LayoutAlignDirective, LayoutDirective } from '@ngbracket/ngx-layout/flex';
import { Store } from '@ngxs/store';
import { ReportControlsComponent } from '@reports/interface/reportControls/report-controls.component';
import { DataReportComponent } from '@reports/reportTypes/dataReport/data-report.component';
import { FeatureReportComponent } from '@reports/reportTypes/featureReport/feature-report.component';
import { IsolatedReportService } from '@reports/services/isolatedReport.service';
import { SelectReport } from '@reports/state/report.actions';
import {
    DataReportLaunchOptions,
    FeatureReportLaunchOptions,
    ReportDefinition,
    ReportLaunchOptions,
    ReportTypeValue
} from '@reports/state/report.model';
import { ReportState } from '@reports/state/report.state';
import { LoggerService } from '@shared/services/logger.service';
import { isEqual } from 'lodash-es';
import { Observable } from 'rxjs';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';

@Component({
    selector: 'dash-gis-report-selector',
    templateUrl: './report-selector.component.html',
    styleUrls: [ './report-selector.component.scss' ],
    providers: [ IsolatedReportService ],
    imports: [
        LayoutDirective,
        FlexDirective,
        LayoutAlignDirective,
        NgClass,
        ClassDirective,
        MatFormField,
        MatLabel,
        MatSelect,
        FormsModule,
        MatOption,
        ReportControlsComponent,
        NgTemplateOutlet,
        FeatureReportComponent,
        DataReportComponent,
        AsyncPipe
    ]
})
export class ReportSelectorComponent implements OnInit {
    recordCount = output<number>();
    reportTitle = output<string>();
    widthUpdate = output<number>();

    @Input() launchOptions: ReportLaunchOptions[];
    @Input() reportType: ReportTypeValue;
    @Input() uid: string;

    selectedFeatureReports$: Observable<(layer: string) => FeatureReportLaunchOptions> = inject(Store).select(
        ReportState.selectedFeatureReport
    );
    selectedDataReport$: Observable<DataReportLaunchOptions> = inject(Store).select(ReportState.selectedDataReport);

    constructor(private logger: LoggerService, private isolatedReport: IsolatedReportService, private store: Store) {}

    ngOnInit() {
        if (!this.reportType) {
            throw new Error('ReportSelectorComponent: attribute [reportType] is required');
        }
        if (!this.launchOptions) {
            throw new Error('ReportSelectorComponent: attribute [launchOptions] is required');
        }

        // clear report title when history action results in no selected report
        this.selectedReport$.pipe(distinctUntilChanged(isEqual)).subscribe((selectedReport: ReportLaunchOptions) => {
            if (!selectedReport) {
                this.reportTitle.emit(null);
                this.recordCount.emit(0);
            } else {
                this.reportTitle.emit(selectedReport.definition.displayName);
            }
        });
    }

    reportCompareFn(c1: ReportDefinition, c2: ReportDefinition) {
        return c1 && c2 && c1.name === c2.name;
    }

    setSelectedReport(selected: ReportDefinition) {
        if (selected) {
            const launchOptions: ReportLaunchOptions = { definition: selected };
            this.store.dispatch(new SelectReport({ selectedReport: launchOptions }));
        }
    }

    setRecordCount(count: number) {
        this.recordCount.emit(count);
    }

    clearReports() {
        this.reportTitle.emit(null);
        this.setRecordCount(0);
    }

    createUid(...args: any[]) {
        return args.join('_');
    }

    get layer() {
        if (this.reportType === 'feature' && this.launchOptions.length > 0) {
            return this.launchOptions[0].definition.layer;
        }
        return null;
    }

    get objectKey() {
        return this.isolatedReport.objectKey;
    }

    get visibleDefinitions(): ReportDefinition[] {
        return this.launchOptions.map((o) => o.definition).filter((d) => d && !d.hidden);
    }

    get selectedReport$(): Observable<ReportLaunchOptions> {
        switch (this.reportType.toLowerCase()) {
            case 'feature':
                return this.selectedFeatureReports$.pipe(map((fn) => fn(this.launchOptions[0].definition.layer)));
            case 'data':
                return this.selectedDataReport$.pipe(filter((obj) => !!obj));
        }
    }

    get selectedReportDefinition$(): Observable<ReportDefinition> {
        return this.selectedReport$.pipe(map((r) => r && r.definition));
    }

    get digitizeKey$() {
        return this.isolatedReport.digitizeKey$;
    }

    protected onWidthUpdate(width: number) {
        this.widthUpdate.emit(width);
    }
}
